import { PREVIEW_EMAIL_OVERRIDE_MISSING_CODE } from "@/utils/previewEmailOverride";

const FAIL_FAST_ENDPOINTS_ON_SERVER_ERROR = new Set([
  "getAdminV2RevenueSubscriptions",
  "getAdminV2Me",
  "getAdminV2RevenueAccessContext",
]);

const FAIL_FAST_ENDPOINTS_ON_TRANSPORT_ERROR = new Set([
  "getAdminV2Me",
  "getAdminV2RevenueAccessContext",
]);

// PostHog dashboards already surface a deterministic inline warning state for
// missing config, so retrying the same expected 503 just adds log noise and
// extra backend load without improving the operator experience.
// See `apps/admin/src/__tests__/pages/AdminV2InsightsRoute.test.tsx`.
const FAIL_FAST_ENDPOINT_ERROR_CODES = new Map([
  ["getAdminV2PosthogDashboards", new Set(["posthog_not_configured"])],
  ["sendBulkEmail", new Set([PREVIEW_EMAIL_OVERRIDE_MISSING_CODE])],
]);

const ACCESS_CHECK_TRANSPORT_ENDPOINTS = new Set([
  "/admin/v2/me",
  "/admin/v2/revenue/access-context",
]);

const ACCESS_PREFLIGHT_TIMEOUT_MS = 10_000;
const MESSAGING_EMAIL_AUTHORING_PREVIEW_TIMEOUT_MS = 4 * 60 * 1000;
const MESSAGING_EMAIL_AUTHORING_PREVIEW_ENDPOINT =
  "/admin/v2/messaging/email-authoring/preview";
const MESSAGING_TEMPLATE_HTML_IMPORT_TIMEOUT_MS = 15 * 60 * 1000;
const MESSAGING_TEMPLATE_HTML_IMPORT_ENDPOINT =
  "/admin/v2/messaging/templates/import-html/preview";

const PARTNER_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN =
  /^\/admin\/partners\/[^/]+$/;
const FEATURE_REQUEST_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN =
  /^\/admin\/feature-requests\/[^/]+$/;
const MESSAGING_PERSON_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN =
  /^\/admin\/v2\/messaging\/(?:preferences|in-app)\/people\/[^/]+$/;
const EMAIL_ACTIVITY_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN =
  /^\/admin\/v2\/messaging\/email-activity\/recipients\/[^/]+$/;
const DEMO_ACCESS_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN =
  /^\/admin\/demo-access(?:\/[^/]+)?$/;
// The Insights workspace renders a deterministic inline fallback for this
// bootstrap state, so the shared API logger should treat it as degraded
// integration availability rather than a hard frontend failure.
// See `apps/admin/src/__tests__/pages/AdminV2InsightsRoute.test.tsx`.
const POSTHOG_DASHBOARDS_EXPECTED_NOT_CONFIGURED_ENDPOINT_PATTERN =
  /^\/admin\/v2\/insights\/posthog\/dashboards$/;

export const isRetryableHttpStatus = (status: number): boolean => {
  // Retry only when we have a reasonable chance of succeeding on a subsequent attempt.
  // 4xx errors generally indicate a client/input/auth issue and should not be retried.
  if (status === 408) return true; // Request Timeout
  if (status === 429) return true; // Rate limited
  return status >= 500;
};

export const isAccessCheckTransportEndpoint = (endpoint: string): boolean =>
  ACCESS_CHECK_TRANSPORT_ENDPOINTS.has(endpoint);

export const resolveRequestTimeoutMs = (url: string): number => {
  const normalizedUrl = `/${url.replace(/^\/+/, "").replace(/\/+$/, "")}`;
  if (ACCESS_CHECK_TRANSPORT_ENDPOINTS.has(normalizedUrl)) {
    // Route-level access preflight checks should fail fast into explicit
    // recovery UI rather than leave operators in an extended loading state.
    return ACCESS_PREFLIGHT_TIMEOUT_MS;
  }
  if (normalizedUrl === MESSAGING_EMAIL_AUTHORING_PREVIEW_ENDPOINT) {
    // CRITICAL: a write can run library, Help and draft stages in sequence,
    // each with a 60s provider deadline. Keep one minute for local work so the
    // browser does not discard a still-running paid result.
    // docs/admin/requirements/email-amendment-20261013.md
    return MESSAGING_EMAIL_AUTHORING_PREVIEW_TIMEOUT_MS;
  }
  if (normalizedUrl === MESSAGING_TEMPLATE_HTML_IMPORT_ENDPOINT) {
    // Luna may use three bounded server-owned repair attempts under one
    // request. Keep the client alive through that contract so a paid result
    // is not discarded after the ordinary API timeout.
    return MESSAGING_TEMPLATE_HTML_IMPORT_TIMEOUT_MS;
  }

  return 60_000;
};

export const shouldFailFastForExpectedErrorCode = (args: {
  endpoint: string | null;
  errorCode: string | null;
}): boolean => {
  if (args.endpoint === null || args.errorCode === null) {
    return false;
  }

  return (
    FAIL_FAST_ENDPOINT_ERROR_CODES.get(args.endpoint)?.has(args.errorCode) ??
    false
  );
};

export const shouldFailFastForServerErrorEndpoint = (args: {
  endpoint: string | null;
  status: unknown;
}): boolean =>
  typeof args.status === "number" &&
  args.status >= 500 &&
  args.endpoint !== null &&
  FAIL_FAST_ENDPOINTS_ON_SERVER_ERROR.has(args.endpoint);

export const shouldFailFastForTransportErrorEndpoint = (
  endpoint: string | null,
): boolean =>
  endpoint !== null && FAIL_FAST_ENDPOINTS_ON_TRANSPORT_ERROR.has(endpoint);

export const shouldReduceAccessCheckOutageNoise = (args: {
  endpoint: string;
  hasHttpResponse: boolean;
  errorCode?: string;
  errorMessage: string;
  isOffline: boolean;
}): boolean => {
  if (!ACCESS_CHECK_TRANSPORT_ENDPOINTS.has(args.endpoint)) {
    return false;
  }

  if (args.hasHttpResponse) {
    return false;
  }

  if (args.isOffline) {
    return true;
  }

  if (args.errorCode === "ERR_NETWORK") {
    return true;
  }

  return /ERR_CONNECTION_REFUSED|Network Error/i.test(args.errorMessage);
};

export const shouldEmitApiHealthNetworkFailure = (args: {
  hasHttpResponse: boolean;
  errorCode?: string;
  errorMessage: string;
  isOffline: boolean;
}): boolean => {
  if (args.hasHttpResponse) {
    return false;
  }

  if (args.isOffline) {
    return true;
  }

  if (args.errorCode === "ERR_NETWORK" || args.errorCode === "ECONNABORTED") {
    return true;
  }

  return /ERR_CONNECTION_REFUSED|Network Error|Failed to fetch|Load failed/i.test(
    args.errorMessage,
  );
};

export const resolveExpectedHandledNoiseReason = (args: {
  endpoint: string;
  statusCode?: number;
  publicErrorCode?: string;
}): string | null => {
  if (
    args.statusCode === 404 &&
    args.publicErrorCode === "NOT_FOUND" &&
    DEMO_ACCESS_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN.test(args.endpoint)
  ) {
    return "EXPECTED_DEMO_ACCESS_TARGET_NOT_FOUND";
  }

  return null;
};

export const resolveExpectedDegradedNoiseReason = (args: {
  endpoint: string;
  statusCode?: number;
  publicErrorCode?: string;
}): string | null => {
  if (args.statusCode === 404) {
    if (
      args.publicErrorCode === "PARTNER_NOT_FOUND" &&
      PARTNER_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN.test(args.endpoint)
    ) {
      return "EXPECTED_PARTNER_NOT_FOUND";
    }

    if (
      args.publicErrorCode === "feature_request_not_found" &&
      FEATURE_REQUEST_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN.test(args.endpoint)
    ) {
      return "EXPECTED_FEATURE_REQUEST_NOT_FOUND";
    }

    if (
      args.publicErrorCode === "message_person_not_found" &&
      MESSAGING_PERSON_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN.test(args.endpoint)
    ) {
      return "EXPECTED_MESSAGING_PERSON_NOT_FOUND";
    }

    if (
      EMAIL_ACTIVITY_EXPECTED_NOT_FOUND_ENDPOINT_PATTERN.test(args.endpoint)
    ) {
      return "EXPECTED_EMAIL_ACTIVITY_NOT_FOUND";
    }
  }

  if (
    args.statusCode === 503 &&
    args.publicErrorCode === "posthog_not_configured" &&
    POSTHOG_DASHBOARDS_EXPECTED_NOT_CONFIGURED_ENDPOINT_PATTERN.test(
      args.endpoint,
    )
  ) {
    return "EXPECTED_POSTHOG_NOT_CONFIGURED";
  }

  return null;
};
